import React, { useState, useEffect } from 'react';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAllConfigs } from '../../services/configService';
import { useAuth } from '../../context/AuthContext';

/**
 * MaintenanceBanner Component
 *
 * Displays a warning banner when maintenance mode is enabled.
 * Only visible to non-admin users (admins can bypass maintenance mode).
 */
const MaintenanceBanner = () => {
  const { t, i18n } = useTranslation();
  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchMaintenanceConfig = async () => {
      try {
        const configResponse = await getAllConfigs();
        if (configResponse.success && configResponse.data?.maintenance) {
          setMaintenanceConfig(configResponse.data.maintenance);
        }
      } catch (error) {
        console.error('Error fetching maintenance config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMaintenanceConfig();

    // Refresh config every 60 seconds to catch changes
    const interval = setInterval(fetchMaintenanceConfig, 60000);
    return () => clearInterval(interval);
  }, []);

  // Don't show banner if loading, maintenance is disabled, or user is admin
  if (loading || !maintenanceConfig?.enabled || user?.is_admin) {
    return null;
  }

  // Follow the language the visitor picked, with EN as the fallback the admin
  // always fills in; navigator.language is ignored on purpose.
  const activeLang = (i18n.language || 'EN').toUpperCase().slice(0, 2);
  const message = maintenanceConfig.message?.[activeLang]
    || maintenanceConfig.message?.EN
    || t('nav.maintenanceDefault');

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 1200, width: '100%' }}>
      <Alert
        severity="warning"
        sx={{
          borderRadius: 0,
          '& .MuiAlert-message': {
            width: '100%',
            textAlign: 'center'
          }
        }}
      >
        {message}
      </Alert>
    </Box>
  );
};

export default MaintenanceBanner;
